import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  listDocuments, uploadDocument, archiveDocument, documentDownloadUrl,
} from "../../api/client";
import { DocumentRecord } from "../../types";

/**
 * Reusable PDF document list + uploader for any entity.
 *
 * Drop it anywhere with the entity it attaches to:
 *   <DocumentsPanel entityType="company" entityId={company.id} />
 *   <DocumentsPanel entityType="potw_profile" entityId={profile.id}
 *                   category="certifier_agreement" title="Authorization on file" />
 *
 * Bytes live on the upload volume; this only ever moves metadata + the file
 * itself. Archiving is a soft-delete (compliance retention), never a hard delete.
 */
interface Props {
  entityType: string;
  entityId: number;
  /** Default category tag applied to uploads (e.g. "certifier_agreement"). */
  category?: string;
  title?: string;
  /** Caller can hide its own status; otherwise we render inline messages. */
  onChange?: () => void;
  /** List and open only — no upload or archive (e.g. an industrial user viewing their lab report). */
  readOnly?: boolean;
  /** Shown instead of "No documents uploaded yet." */
  emptyText?: string;
}

const fmtSize = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : "";

const DocumentsPanel: React.FC<Props> = ({ entityType, entityId, category, title, onChange, readOnly = false, emptyText }) => {
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    listDocuments(entityType, entityId)
      .then(r => setDocs(r.data))
      .catch(() => {});
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";   // allow re-selecting the same file
    if (!file) return;
    setError("");
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are accepted.");
      return;
    }
    setBusy(true);
    try {
      await uploadDocument(entityType, entityId, file, category);
      load();
      onChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (doc: DocumentRecord) => {
    if (!window.confirm(`Archive "${doc.filename}"? It stays on record but is hidden from this list.`)) return;
    setError("");
    try {
      await archiveDocument(doc.id);
      load();
      onChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? "Could not archive document.");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#4a5568",
                       textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {title ?? `Documents (${docs.length})`}
        </span>
        {!readOnly && (
          <label style={{ ...btn, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Uploading…" : "+ Upload PDF"}
            <input ref={fileRef} type="file" accept="application/pdf,.pdf"
                   onChange={onPick} disabled={busy} style={{ display: "none" }} />
          </label>
        )}
      </div>

      {error && <p style={{ color: "#c53030", fontSize: 12, margin: "6px 0 0" }}>{error}</p>}

      {docs.length === 0
        ? <p style={{ color: "#718096", fontSize: 13, margin: "6px 0 0" }}>{emptyText ?? "No documents uploaded yet."}</p>
        : <div style={{ marginTop: 6 }}>
            {docs.map(d => (
              <div key={d.id} style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", gap: 8, fontSize: 13, padding: "6px 0",
                    borderBottom: "1px solid #eee" }}>
                <span style={{ minWidth: 0 }}>
                  <a href={documentDownloadUrl(d.id)} target="_blank" rel="noopener noreferrer"
                     style={{ fontWeight: 600, color: "#2b6cb0", wordBreak: "break-all" }}>
                    {d.filename}
                  </a>
                  <span style={{ color: "#718096", marginLeft: 8 }}>
                    {fmtSize(d.size_bytes)}
                    {d.category ? ` · ${d.category}` : ""}
                    {d.uploaded_at ? ` · ${fmtDate(d.uploaded_at)}` : ""}
                    {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}
                  </span>
                </span>
                {!readOnly && (
                  <button type="button" onClick={() => onArchive(d)}
                    style={{ ...outlineBtn, padding: "2px 10px", fontSize: 12 }}>Archive</button>
                )}
              </div>
            ))}
          </div>}
    </div>
  );
};

const btn: React.CSSProperties = {
  background: "#3182ce", color: "#fff", border: "none", borderRadius: 6,
  padding: "4px 12px", fontSize: 13, fontWeight: 600,
};
const outlineBtn: React.CSSProperties = {
  background: "#fff", color: "#4a5568", border: "1px solid #cbd5e0",
  borderRadius: 6, cursor: "pointer",
};

export default DocumentsPanel;
